import cn from "classnames"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { merchantSelectors, sortAscending, SpinnerContainer, TextButton, useSelector } from "swiipe.portal.shared"
import { gatewaySelectors, IGatewayDto } from "../../store/reducers/gatewayReducer"
import { StoreState } from "../../store/StoreState"
import { getGatewaysForMerchantThunk } from "../../store/thunks/gatewayThunks"
import { setIsGatewayPrimaryThunk, triggerOnboardingCacheClearThunk } from "../../store/thunks/swiipePaymentsThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import "./GatewaysConfiguration.scss"

export interface IGatewaysConfiguration {
    webshopId: string
    className?: string
}

export const GatewaysConfiguration = ({ webshopId, className }: IGatewaysConfiguration) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const gateways = useSelector((state: StoreState) =>
        gatewaySelectors.getGatewaysForMerchant(state, merchantDetails?.swMerchant.swMerchantId ?? "")
    )
    const gatewaysForWebshop = gateways?.filter((gw) => gw.webshopId === webshopId) ?? []

    useEffect(() => {
        if (merchantDetails) {
            dispatch(getGatewaysForMerchantThunk(merchantDetails.swMerchant.swMerchantId, false))
        }
    }, [merchantDetails])

    if (!merchantDetails || !merchantDetails.webshops.find((w) => w.webshopId === webshopId)) {
        return null
    }

    const GatewayRow = ({
        gateway,
        titles,
    }: {
        gateway?: IGatewayDto
        titles?: { paymentType: string; providerType: string; enabled: string; status: string; isMain: string }
    }) => {
        return (
            <div className={cn("gateways-configuration__gateway", !!titles && "gateways-configuration__gateway-header")}>
                <div className="gateways-configuration__payment-type">
                    {gateway?.paymentType}
                    {titles?.paymentType}
                </div>
                <div className="gateways-configuration__provider-type">
                    {gateway?.providerType}
                    {titles?.providerType}
                </div>
                <div className="gateways-configuration__enabled">
                    {!!gateway && (gateway.enabled ? "Yes" : "No")}
                    {titles?.enabled}
                </div>
                <div className="gateways-configuration__status">
                    {!!gateway && (gateway?.onboardingStatus ?? "None")}
                    {titles?.status}
                </div>
                <div className="gateways-configuration__primary">
                    {!!gateway && (gateway?.isMainGatewayForPaymentType ? "Yes" : "No")}
                    {titles?.isMain}
                    {gateway && !gateway?.isMainGatewayForPaymentType && (
                        <TextButton
                            text={"Use as main"}
                            textDecoration="underline"
                            className="gateways-configuration__primary-button"
                            onClick={async () => {
                                try {
                                    await dispatch(
                                        setIsGatewayPrimaryThunk(
                                            merchantDetails.swMerchant.swMerchantId,
                                            webshopId,
                                            gateway.gatewayId,
                                            true
                                        )
                                    )
                                } catch {
                                    //Catch to stop showing spinner
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        )
    }

    return (
        <SpinnerContainer showSpinner={!gateways} minHeight={40}>
            <div className={cn("gateways-configuration", className)}>
                <GatewayRow
                    titles={{
                        enabled: "Is enabled",
                        isMain: "Is main",
                        paymentType: "Payment type",
                        providerType: "Provider",
                        status: "Onb. status",
                    }}
                />
                {sortAscending(gatewaysForWebshop, (gw) => gw.paymentType).map((gw) => (
                    <GatewayRow key={gw.gatewayId} gateway={gw} />
                ))}
                {gatewaysForWebshop.length > 0 && (
                    <TextButton
                        className="gateways-configuration__cache-clear"
                        text={"Cache clear payment onboarding"}
                        textDecoration="underline"
                        onClick={async () => {
                            try {
                                await dispatch(
                                    triggerOnboardingCacheClearThunk(
                                        merchantDetails.swMerchant.swMerchantId,
                                        webshopId,
                                        gatewaysForWebshop[0].gatewayId
                                    )
                                )
                            } catch {
                                //Catch to stop showing spinner
                            }
                        }}
                    />
                )}
            </div>
        </SpinnerContainer>
    )
}

export default GatewaysConfiguration
